/**
 * Auth-aware API client with envelope normalization and refresh-on-401 retry.
 *
 * Token storage:
 *   - access  → in-memory only
 *   - refresh → localStorage (survives reload, cleared on logout)
 *
 * Envelope:
 *   success → { success: true,  data: T,  message?: string }
 *   error   → { success: false, error: { code, message, details? } }
 */
import { env } from "./env";

const REFRESH_STORAGE_KEY = "odblox.refresh";

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export interface ApiSuccess<T> {
  success: true;
  data: T;
  message?: string;
}

export interface ApiFailure {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure;

export class ApiError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;

  constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setRefreshToken(token: string | null): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  if (token) {
    localStorage.setItem(REFRESH_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_STORAGE_KEY);
  }
}

export function getRefreshToken(): string | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  return localStorage.getItem(REFRESH_STORAGE_KEY);
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

function buildHeaders(init: RequestInit, skipAuth: boolean): Headers {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (!skipAuth && accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  return headers;
}

async function parseEnvelope<T>(res: Response): Promise<ApiEnvelope<T>> {
  const text = await res.text();
  if (!text) {
    if (res.ok) {
      return { success: true, data: undefined as unknown as T };
    }
    return {
      success: false,
      error: { code: "empty_response", message: "Empty response" },
    };
  }
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      success: false,
      error: { code: "invalid_response", message: text.slice(0, 200) },
    };
  }
}

async function refreshAccessOnce(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) {
    return false;
  }
  const res = await fetch(`${env.API_BASE_URL}/api/admin/v1/auth/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });
  const envelope = await parseEnvelope<{ access: string; refresh?: string }>(res);
  if (!envelope.success) {
    return false;
  }
  setAccessToken(envelope.data.access);
  if (envelope.data.refresh) {
    setRefreshToken(envelope.data.refresh);
  }
  return true;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
  skipAuth?: boolean;
  noRetry?: boolean;
}

async function handleResponse<T>(res: Response): Promise<T> {
  const envelope = await parseEnvelope<T>(res);
  if (envelope.success) {
    return envelope.data;
  }
  throw new ApiError(
    envelope.error.code,
    envelope.error.message,
    res.status,
    envelope.error.details,
  );
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, skipAuth = false, noRetry = false, ...rest } = options;
  const init: RequestInit = { ...rest };
  if (json !== undefined) {
    init.body = JSON.stringify(json);
  }
  init.headers = buildHeaders(init, skipAuth);

  const url = `${env.API_BASE_URL}${path}`;
  const res = await fetch(url, init);

  if (res.status === 401 && !skipAuth && !noRetry) {
    const refreshed = await refreshAccessOnce();
    if (refreshed) {
      init.headers = buildHeaders(init, false);
      const retry = await fetch(url, init);
      return handleResponse<T>(retry);
    }
    setAccessToken(null);
    setRefreshToken(null);
    onUnauthorized?.();
  }

  return handleResponse<T>(res);
}

export interface HealthResponse {
  ok: boolean;
  db: boolean;
  redis: boolean;
}

export interface CompanyEmbed {
  id: string;
  name: string;
  slug: string;
  domain: string;
}

export type Role = "superadmin" | "platform_admin" | "member";

export interface CurrentUser {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  company: CompanyEmbed;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: CurrentUser;
}

export const api = {
  health: async (): Promise<HealthResponse> => {
    const res = await fetch(`${env.API_BASE_URL}/health/`);
    const text = await res.text();
    try {
      return JSON.parse(text) as HealthResponse;
    } catch {
      throw new ApiError("invalid_response", "Health endpoint returned invalid JSON", res.status);
    }
  },

  login: (email: string, password: string) =>
    request<LoginResponse>("/api/admin/v1/auth/login/", {
      method: "POST",
      json: { email, password },
      skipAuth: true,
    }),

  me: () => request<CurrentUser>("/api/admin/v1/auth/me/"),

  logout: (refresh: string) =>
    request<undefined>("/api/admin/v1/auth/logout/", {
      method: "POST",
      json: { refresh },
    }),
};
